<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ApiRestaurant } from '@/api/types/location'
import BranchCard from '@/components/location/BranchCard.vue'
import BranchList from '@/components/location/BranchList.vue'
import LocationHeader from '@/components/location/LocationHeader.vue'
import MapCanvas from '@/components/start/MapCanvas.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import { useGeoPoint } from '@/composables/useGeoPoint'
import type { LngLat } from '@/composables/useLocation'
import type { MapMarker } from '@/composables/useRestaurants'

const props = withDefaults(defineProps<Props>(), { saving: false, center: undefined })

const emit = defineEmits<{
  confirm: [value: { restaurantId: string }]
  back: []
}>()

const { t } = useI18n()

/**
 * Самовывоз: филиалы на карте и списком. Выбор филиала раскрывает карточку
 * со временем работы, адресом и телефоном — подтверждение только оттуда,
 * чтобы клиент увидел, докуда ему ехать.
 */
interface Props {
  branches: ApiRestaurant[]
  markers: MapMarker[]
  saving?: boolean
  center?: LngLat | undefined
}

const { locate } = useGeoPoint()

const activeId = ref('')
const mapCenter = ref<LngLat>(props.center ?? [0, 0])
const origin = ref<LngLat>()
const query = ref('')
/** Шит из макета живёт в двух высотах: 300px и почти во весь экран. */
const expanded = ref(false)

/** Низкая высота из макета и зазор, на который сверху видно карту. */
const SHORT_HEIGHT = 300
const TOP_GAP = 40

/** Бросок пальцем: выше этой скорости решает направление, а не пройденный путь. */
const FLING = 0.4

const body = useTemplateRef<HTMLElement>('body')
const sheet = useTemplateRef<HTMLElement>('sheet')

/**
 * Высота во время перетаскивания. Пока тянут — шит идёт за пальцем, поэтому
 * анимацию на это время снимаем: иначе она сглаживает каждый кадр и движение
 * отстаёт от руки. Отпустили — высоту отдаём обратно классу, и он доводит
 * шит до ближайшего состояния уже с переходом.
 */
const dragHeight = ref<number | null>(null)

/** Во весь экран минус зазор на карту; без измеренного контейнера — как есть. */
function tallHeight(): number {
  return Math.max(SHORT_HEIGHT, (body.value?.clientHeight ?? 0) - TOP_GAP)
}

let startY = 0
let startHeight = 0
let lastY = 0
let lastAt = 0
/** px/мс, вниз — положительная */
let speed = 0
let moved = false

function dragStart(event: TouchEvent): void {
  const touch = event.touches[0]
  if (!touch) return

  startY = touch.clientY
  lastY = touch.clientY
  lastAt = event.timeStamp
  speed = 0
  moved = false
  startHeight = sheet.value?.offsetHeight ?? SHORT_HEIGHT
  dragHeight.value = startHeight
}

function dragMove(event: TouchEvent): void {
  const touch = event.touches[0]
  if (dragHeight.value === null || !touch) return

  const shift = touch.clientY - startY
  if (Math.abs(shift) > 3) moved = true

  const gap = event.timeStamp - lastAt
  if (gap > 0) speed = (touch.clientY - lastY) / gap
  lastY = touch.clientY
  lastAt = event.timeStamp

  dragHeight.value = Math.min(Math.max(startHeight - shift, SHORT_HEIGHT), tallHeight())
}

/**
 * Куда довести шит. Быстрый бросок слушаем в первую очередь — так ведут себя
 * нативные шиты: короткий резкий свайп вниз закрывает, даже если палец прошёл
 * всего ничего. Спокойное перетаскивание решается ближайшей высотой.
 */
function dragEnd(): void {
  if (dragHeight.value === null) return

  const height = dragHeight.value
  dragHeight.value = null

  if (Math.abs(speed) > FLING) {
    expanded.value = speed < 0
    return
  }

  expanded.value = height > (SHORT_HEIGHT + tallHeight()) / 2
}

/** Тап по ручке переключает высоту, но только если это был тап, а не свайп. */
function toggle(): void {
  if (!moved) expanded.value = !expanded.value
}

const selected = computed(() => props.branches.find((branch) => branch.id === activeId.value))

function select(marker: MapMarker): void {
  activeId.value = marker.id
  mapCenter.value = marker.coordinates
}

/** Со стрелки в шапке возвращаемся к списку, а с него — наружу. */
function back(): void {
  if (activeId.value) activeId.value = ''
  else emit('back')
}

/**
 * «Показать ближайшие»: шит раскрываем сразу, не дожидаясь геолокации —
 * она может думать секунду или спросить разрешение, а список должен
 * ответить на тап мгновенно. Придут координаты — он пересортируется.
 */
async function findMe(): Promise<void> {
  expanded.value = true

  const point = await locate()
  if (!point) return
  origin.value = point
  mapCenter.value = point
}
</script>

<template>
  <section class="branch-picker">
    <LocationHeader :title="selected?.name ?? t('branch.where')" @back="back" />

    <div ref="body" class="branch-picker__body">
      <MapCanvas
        class="branch-picker__canvas"
        :center="mapCenter"
        :marker="false"
        :markers="markers"
        :active-marker-id="activeId"
        @marker-click="select"
      />

      <BranchCard
        v-if="selected"
        :branch="selected"
        :loading="saving"
        class="branch-picker__card"
        @confirm="emit('confirm', { restaurantId: activeId })"
      />

      <div
        v-else
        ref="sheet"
        class="branch-picker__sheet"
        :class="{ 'branch-picker__sheet--tall': expanded, 'branch-picker__sheet--dragged': dragHeight !== null }"
        :style="dragHeight === null ? undefined : { height: `${dragHeight}px` }"
      >
        <!-- Тянут за всю шапку, а не за полоску: по 4px пальцем не попасть -->
        <div
          class="branch-picker__grip"
          @touchstart.passive="dragStart"
          @touchmove.prevent="dragMove"
          @touchend="dragEnd"
          @touchcancel="dragEnd"
        >
          <button
            type="button"
            class="branch-picker__handle"
            :aria-label="t('common.expand')"
            @click="toggle"
          />
          <h2 class="branch-picker__title">{{ t('branch.title') }}</h2>
        </div>

        <div class="branch-picker__head">
<label class="branch-picker__search">
            <AppIcon name="search" :size="24" class="branch-picker__search-icon" />
            <input
              v-model="query"
              type="text"
              :placeholder="t('branch.search')"
              @focus="expanded = true"
            />
          </label>
        </div>

        <div class="branch-picker__block">
          <BranchList
            :branches="markers"
            :query="query"
            :origin="origin"
            @select="select"
            @locate="findMe"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.branch-picker {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;

  /* Карта во всю площадь, шит лежит поверх — так пины видно из-под него */
  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  &__canvas {
    position: absolute;
    inset: 0;
  }

  /* Два состояния из макета: 300px и во весь экран без 40px карты сверху */
  &__sheet,
  &__card {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &__sheet {
    display: flex;
    flex-direction: column;
    height: 300px;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-muted);
    transition: height 0.25s ease;

    &--tall {
      height: calc(100% - 40px);
    }

    /* Пока тянут — высоту задаёт палец, переход только мешал бы за ним успевать */
    &--dragged {
      transition: none;
    }
  }

  /* Карточка филиала по высоте контента, но не выше карты */
  &__card {
    max-height: calc(100% - 40px);
  }

  /*
   * Зона перетаскивания — ручка и заголовок целиком: по полоске в 4px
   * пальцем не попасть. `touch-action: none` обязателен, иначе браузер
   * заберёт вертикальный жест себе и шит дёрнется только в конце.
   */
  &__grip {
    padding: var(--space-3) var(--screen-padding) 0;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
    touch-action: none;
    user-select: none;
  }

  /* Липкая часть: ручка, заголовок и поиск — 129px по макету */
  &__head {
    padding: 0 var(--screen-padding) var(--screen-padding);
    background: var(--surface-card);
  }

  &__handle {
    position: relative;
    display: block;
    width: 50px;
    height: 4px;
    margin: 0 auto var(--space-3);
    padding: 0;
    border: 0;
    border-radius: var(--radius-xl);
    background: var(--surface-inverse);
    cursor: pointer;

    /* Нажатие ловим с запасом, а видно по-прежнему полоску 50×4 */
    &::before {
      content: '';
      position: absolute;
      inset: -18px -40px;
    }
  }

  &__title {
    margin-bottom: var(--space-5);
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  &__search {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    height: 50px;
    padding: 0 var(--field-padding-x);
    border-radius: var(--radius-xl);
    background: var(--surface-muted);
    cursor: text;

    input {
      width: 100%;
      padding: 0;
      border: 0;
      background: none;
      color: var(--text-color);
      caret-color: var(--accent-500);
      font-family: inherit;
      font-size: 13px;
      font-weight: var(--fw-medium);
      line-height: 20px;
      outline: none;

      &::placeholder {
        color: var(--text-color-secondary);
      }
    }
  }

  &__search-icon {
    color: var(--icon-color);
  }

  /* Прокручиваемый блок отделён от липкой шапки полоской фона в 4px */
  &__block {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    margin-top: 4px;
    padding: 0 var(--screen-padding);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }
}
</style>
